/**
 * Bug context capture module.
 * Monkey-patches console.error on import to capture recent errors.
 * Exports helpers to record failed API calls and collect a full snapshot.
 */

const MAX_ENTRIES = 10;
const consoleErrors = [];
const failedRequests = [];

// Patch console.error to capture messages
const _origError = console.error;
console.error = (...args) => {
  try {
    const message = args.map(a =>
      typeof a === 'string' ? a : a instanceof Error ? a.message : JSON.stringify(a)
    ).join(' ');
    consoleErrors.push({ message: message.slice(0, 500), timestamp: new Date().toISOString() });
    if (consoleErrors.length > MAX_ENTRIES) consoleErrors.shift();
  } catch (_) {
    // never break the app
  }
  _origError.apply(console, args);
};

/**
 * Called from axios error interceptor to record a failed API request.
 */
export function recordFailedRequest(config, error) {
  try {
    failedRequests.push({
      method: config?.method?.toUpperCase() || 'UNKNOWN',
      url: config?.url || 'unknown',
      status: error?.response?.status || null,
      message: error?.message || 'Unknown error',
      timestamp: new Date().toISOString(),
    });
    if (failedRequests.length > MAX_ENTRIES) failedRequests.shift();
  } catch (_) {
    // never break the app
  }
}

// Known route patterns for extracting IDs from pathname
const ROUTE_PATTERNS = [
  { pattern: /\/learn\/deck\/([^/]+)\/levels/, key: 'deckId' },
  { pattern: /\/learn\/deck\/([^/]+)\/boss/, key: 'deckId' },
  { pattern: /\/learn\/deck\/([^/]+)\/level\/([^/]+)/, keys: ['deckId', 'subtopicId'] },
  { pattern: /\/learn\/boss\/final\/([^/]+)/, key: 'examId' },
  { pattern: /\/quiz\/([^/]+)/, key: 'quizId' },
  { pattern: /\/deck\/([^/]+)/, key: 'deckId' },
  { pattern: /\/pvp\/session\/([^/]+)/, key: 'matchId' },
  { pattern: /\/card\/([^/]+)/, key: 'cardId' },
];

function extractUrlParams() {
  const path = window.location.pathname;
  const params = {};
  for (const route of ROUTE_PATTERNS) {
    const m = path.match(route.pattern);
    if (m) {
      if (route.keys) {
        route.keys.forEach((k, i) => { params[k] = m[i + 1]; });
      } else {
        params[route.key] = m[1];
      }
      break;
    }
  }
  return params;
}

function valueOrNull(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

function collectRichContext({ questionContext = null, screenshotAttached = false } = {}) {
  const urlParams = extractUrlParams();
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const blockIndex = valueOrNull(questionContext?.block_index, questionContext?.current_question_index);
  const currentQuestionIndex = valueOrNull(questionContext?.current_question_index, blockIndex);

  return {
    route: {
      page_url: window.location.href,
      deck_id: valueOrNull(questionContext?.deck_id, urlParams.deckId),
      exam_id: valueOrNull(questionContext?.exam_id, urlParams.examId),
      question_id: valueOrNull(questionContext?.question_id, questionContext?.id),
      level_session_id: valueOrNull(questionContext?.level_session_id),
      subtopic_id: valueOrNull(questionContext?.subtopic_id, urlParams.subtopicId),
      block_index: blockIndex,
    },
    session: {
      session_id: valueOrNull(questionContext?.session_id),
      phase: valueOrNull(questionContext?.phase),
      current_question_index: currentQuestionIndex,
    },
    browser: {
      user_agent: navigator.userAgent,
      viewport,
    },
    evidence: {
      screenshot_attached: Boolean(screenshotAttached),
      frontend_errors: [...consoleErrors],
      failed_requests: [...failedRequests],
    },
  };
}

/**
 * Collect a full technical context snapshot.
 * `app_state` is left null here — the caller enriches it.
 */
export function collectBugContext(options = {}) {
  return {
    user_agent: navigator.userAgent,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    timestamp: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    console_errors: [...consoleErrors],
    failed_requests: [...failedRequests],
    url_params: extractUrlParams(),
    app_state: null,
    rich_context: collectRichContext(options),
  };
}
